import React, { useEffect, useState } from 'react';
import { Routes, useNavigate, useParams } from 'react-router-dom';
import {
	ArchiveBoxIcon,
	BeakerIcon,
	TableCellsIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import Modal from '../../components/Shell/Modal';
import Button from '../../components/Button';
import { classNames } from '../../components/helpers/functions';
import DataTable from '../../components/DataTable';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import BetaAttributes from '../../components/Attributes';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';

import useAsync from '../../hooks/useAsync';
import useRequired from '../../hooks/useRequired';
import useSearchParams from '../../hooks/useSearchParams';
import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';

import { getConsumables } from '../../api/consumables';
import { getModuleFields } from '../../api/modules';
import { addStoragePoint, getStoragePoints } from '../../api/storage-points';

import {
	getArrayPosition,
	validateUUID,
} from '../../components/helpers/functions';

import ViewConsumable from './ViewConsumable';
import StoragePoint from './StoragePoint';
import Inventory from './Inventory';
import Picklists from './Picklists';
import Consumable from './Consumable';

const CONSUMABLES_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONSUMABLES;
const STORAGE_POINTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_STORAGE_POINTS;

const Consumables = ({ consumables, onRefreshConsumables, params }) => {
	const navigate = useNavigate();

	const { setOpen: onViewConsumable } = useModal('viewConsumable');
	const { open: isAddConsumableOpen, setOpen: onAddConsumable } =
		useModal('addConsumable');

	const { action } = useParams();

	const { execute: onExportConsumables } = useAsync(getConsumables, {
		immediate: false,
	});

	const prevIsAddConsumableOpen = usePrevious(isAddConsumableOpen);

	useEffect(() => {
		if (action !== null && action === 'add') {
			onAddConsumable({
				moduleId: CONSUMABLES_MODULE_ID,
				onSubmit: () => {
					navigate('/consumables/all');
				},
				onClose: () => navigate(`/consumables/all`),
			});
		} else {
			if (prevIsAddConsumableOpen && !isAddConsumableOpen) {
				onRefreshConsumables();
			}

			if (validateUUID(action)) {
				//onViewConsumable({ id: action, params });
			}
		}
	}, [action]);

	useEffect(() => {
		if (!!prevIsAddConsumableOpen && !isAddConsumableOpen) {
			onRefreshConsumables();
		}
	}, [isAddConsumableOpen]);

	return (
		<>
			<PageHeading
				actions={[
					{
						name: 'Add Consumable',
						onClick: () => navigate(`/consumables/add`),
					},
				]}
				divider={false}
			/>

			<DataTable
				data={consumables}
				columns={[
					{
						header: 'NAME',
						accessorKey: 'name',
						cell: (info) => info?.getValue() || null,
					},
					{
						header: 'VENDOR',
						accessorKey: 'vendor',
						cell: (info) => {
							return info.getValue()?.companyName || null;
						},
						enableFiltering: true,
						filter: {
							key: 'vendorId',
							placeholder: 'Select a vendor',
							options: consumables?.uniqueVendors,
						},
					},
					{
						header: 'CATEGORY',
						accessorKey: 'category',
						cell: (info) => {
							return info.getValue()?.name || null;
						},
						enableFiltering: true,
						filter: {
							key: 'categoryId',
							placeholder: 'Select a category',
							options: consumables?.uniqueCategories,
						},
					},
					...(consumables
						? consumables?.uniqueAttributes?.map((attr) => ({
								header: attr?.label?.toUpperCase(),
								accessorKey: attr?.id,
								cell: (info) => {
									const attribute = info.row?.original?.attributes?.find(
										(o) => o.attribute_field_id === attr?.id
									);
									if (attribute?.vendor) {
										return attribute?.vendor?.companyName;
									} else {
										return attribute?.value || '';
									}
								},
								enableSorting: false,
								column: {
									removable: true,
								},
						  }))
						: []),
				]}
				columnManagementKey="consumablesMainColumns"
				onExport={onExportConsumables}
				exportFileName={`consumables-${moment().format('MM-DD-YYYY')}`}
				onRow={(row) => navigate(`/consumables/${row?.original?.id}`)}
			/>
		</>
	);
};

const configurePayload = (form, fields) => {
	const returnForm = {
		customAttributes: [],
	};

	const updatedFormForDefault = {
		...Object.assign(
			{},
			...fields
				?.filter((field) => field?.isActive && field?.defaultValue)
				.map((field) => ({ [field?.key]: field?.defaultValue }))
		),
		...form,
	};

	Object.keys(updatedFormForDefault).map((key) => {
		const fieldId = updatedFormForDefault[key]?.fieldId;
		const field = fields.find((f) => f.id === fieldId);
		const fieldType = field?.type;
		const isDefault = field?.isDefault || field?.key === 'category';

		let value = field?.defaultValue || null;

		if (fieldType === 'vendor' || field?.key === 'category') {
			value = updatedFormForDefault[key]?.value?.value;
		} else {
			value = updatedFormForDefault[key]?.value;
		}

		if (isDefault) {
			returnForm[key] = value;
		} else {
			if (value) {
				returnForm.customAttributes.push({ fieldId, value, type: fieldType });
			}
		}
	});

	return returnForm;
};

const StoragePoints = () => {
	const navigate = useNavigate();
	const {
		categoryId: categoryFilter,
		locationId: locationFilter,
		query: queryFilter,
	} = useSearchParams();

	const defaultFilters = {
		query: queryFilter,
		categoryId: categoryFilter,
		locationId: locationFilter,
	};

	const action = getArrayPosition(
		history?.location?.pathname?.split('/').filter(Boolean),
		3
	);
	const [isAddModalOpen, setAddModalOpen] = useState(false);
	const [form, setForm] = useState({});

	const { value: storagePoints, execute: refreshStoragePoints } =
		useAsync(getStoragePoints);

	const { execute: onAddStoragePoint, status: isAddingStoragePoint } = useAsync(
		addStoragePoint,
		{ immediate: false, showNotification: true }
	);

	const {
		value: moduleFields,
		execute: onGetModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFields, { immediate: false });

	useEffect(() => {
		if (isAddModalOpen) {
			onGetModuleFields({ id: STORAGE_POINTS_MODULE_ID, active: true });
		} else {
			setForm({});
		}
	}, [isAddModalOpen]);

	useEffect(() => {
		if (action !== null && action === 'add') {
			setAddModalOpen(true);
		} else {
			if (isAddModalOpen) {
				setAddModalOpen(false);
			}
		}
	}, [action]);

	useEffect(() => {
		refreshStoragePoints(defaultFilters);
	}, [
		defaultFilters.categoryId,
		defaultFilters.locationId,
		defaultFilters.query,
	]);

	return (
		<>
			<PageHeading
				actions={[
					{
						name: 'Add Storage Point',
						onClick: () => navigate('/consumables/storage-points/add'),
					},
				]}
				divider={false}
			/>
			<DataTable
				data={storagePoints}
				columns={[
					{
						header: 'NAME',
						accessorKey: 'name',
						cell: (info) => (
							<div>
								{info?.getValue() || null}
								<div className="text-xxs text-gray-400 font-normal">
									{info?.row?.original?.description}
								</div>
							</div>
						),
					},
					{
						header: 'CATEGORY',
						accessorKey: 'category',
						cell: (info) => {
							return info.getValue()?.name || null;
						},
						enableFiltering: true,
						filter: {
							key: 'categoryId',
							placeholder: 'Select a category',
							options: storagePoints?.uniqueCategories,
						},
					},
					{
						header: 'LOCATION',
						accessorKey: 'location',
						cell: (info) => {
							return info.getValue()?.name || null;
						},
						enableFiltering: true,
						filter: {
							key: 'locationId',
							placeholder: 'Select a location',
							options: storagePoints?.uniqueLocations,
						},
					},
				]}
				useExport={false}
				usePagination={false}
				manualSorting={false}
				hasCheckboxes={false}
				onRow={(row) => {
					navigate(`/consumables/storage-points/${row?.original?.id}`);
				}}
			/>

			<Modal
				title="Add Storage Point"
				open={isAddModalOpen}
				onClose={() => navigate('/consumables/storage-points')}
			>
				{isLoadingModuleFields === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
							{moduleFields?.map((field) => (
								<BetaAttributes
									field={field}
									onChange={(index, value) =>
										setForm({ ...form, [index]: { value, fieldId: field.id } })
									}
									form={form}
									moduleId={STORAGE_POINTS_MODULE_ID}
								/>
							))}
						</div>
						<div className="flex justify-end mt-6">
							<Button
								disabled={
									!useRequired(
										form,
										moduleFields
											?.map((field) => field?.isRequired && field?.key)
											.filter(Boolean)
									)
								}
								onClick={() =>
									onAddStoragePoint(
										configurePayload(form, moduleFields),
										() => {
											navigate('/consumables/storage-points');
											refreshStoragePoints();
										}
									)
								}
							>
								{isAddingStoragePoint === 'pending' ? 'Adding' : 'Add'}
							</Button>
						</div>
					</>
				)}
			</Modal>
		</>
	);
};

const BaseConsumables = () => {
	const navigate = useNavigate();
	const { setClose } = useModal('viewConsumable');

	const module = getArrayPosition(
		history?.location?.pathname?.split('/').filter(Boolean),
		2
	);

	const {
		vendorId: vendorFilter,
		categoryId: categoryFilter,
		query: queryFilter,
		page: pageFilter,
		pageSize: pageSizeFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
	} = useSearchParams();

	const defaultFilters = {
		categoryId: categoryFilter,
		vendorId: vendorFilter,
		page: pageFilter || 1,
		pageSize: pageSizeFilter || 10,
		query: queryFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
	};

	//const { value: vendors, execute: onRefreshVendors } = useAsync(getContacts);

	const { value: consumables, execute: onRefreshConsumables } = useAsync(
		getConsumables,
		{
			defaultPayload: defaultFilters,
			silent: true,
		}
	);

	useEffect(() => {
		onRefreshConsumables(defaultFilters);
	}, [
		defaultFilters?.vendorId,
		defaultFilters?.categoryId,
		defaultFilters?.page,
		defaultFilters?.sortBy,
		defaultFilters?.sortOrder,
	]);

	useEffect(() => {
		const delayDebounceFn = setTimeout(
			() => {
				onRefreshConsumables(defaultFilters);
			},
			queryFilter ? 600 : 0
		);

		return () => clearTimeout(delayDebounceFn);
	}, [queryFilter]);

	const prevAddConsumables = usePrevious();

	return (
		<>
			<MainContent>
				<div className="mb-10">
					<dl className="mt-5 mb-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
						<div
							className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 shadow sm:px-6 sm:pt-6 cursor-pointer"
							onClick={() => navigate(`/assets/all`)}
						>
							<dt>
								<div className="absolute rounded-md bg-gray-300 p-2">
									<BeakerIcon
										className="h-6 w-6 text-white"
										aria-hidden="true"
									/>
								</div>
								<p className="ml-16 truncate text-sm font-medium text-gray-500">
									Total Consumables
								</p>
							</dt>
							<dd className="flex items-baseline pb-4">
								<p className="ml-16 text-2xl font-semibold text-gray-900">
									{consumables?.total || 0}
								</p>
							</dd>
						</div>
						<div
							className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 shadow sm:px-6 sm:pt-6 cursor-pointer"
							onClick={() => navigate(`/assets/all?issues=true`)}
						>
							<dt>
								<div
									className={classNames(
										'absolute rounded-md p-3',
										false ? ' bg-red-500' : ' bg-gray-300'
									)}
								>
									<TableCellsIcon
										className="h-6 w-6 text-white"
										aria-hidden="true"
									/>
								</div>
								<p className="ml-16 truncate text-sm font-medium text-gray-500">
									Total Storage Points
								</p>
							</dt>
							<dd className="flex items-baseline pb-4">
								<p className="ml-16 text-2xl font-semibold text-gray-900">0</p>
							</dd>
						</div>
						<div
							className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 shadow sm:px-6 sm:pt-6 cursor-pointer"
							onClick={() => navigate(`/assets/all?issues=true`)}
						>
							<dt>
								<div
									className={classNames(
										'absolute rounded-md p-3',
										false ? ' bg-red-500' : ' bg-gray-300'
									)}
								>
									<ArchiveBoxIcon
										className="h-6 w-6 text-white"
										aria-hidden="true"
									/>
								</div>
								<p className="ml-16 truncate text-sm font-medium text-gray-500">
									Total Storage Locations
								</p>
							</dt>
							<dd className="flex items-baseline pb-4">
								<p className="ml-16 text-2xl font-semibold text-gray-900">0</p>
							</dd>
						</div>
					</dl>
				</div>

				{/*<div>
					<img
						class="h-auto max-w-full"
						src={Floorplan}
						alt="image description"
					/>
									</div>*/}

				<div className="border-b border-gray-200 mb-10 -mt-6">
					<nav className="-mb-px flex space-x-8" aria-label="Tabs">
						{[
							{
								name: 'Inventory',
								current: !module || module === 'inventory',
								//onClick: () => {},
							},
							{
								name: 'Consumables',
								key: 'all',
								current: module === 'all',
								//onClick: () => {},
							},

							{
								name: 'Storage Points',
								key: 'storage-points',
								current: module === 'storage-points',
								//onClick: () => {},
							},
							{
								name: 'Picklists',
								key: 'picklists',
								current: module === 'picklists',
								//onClick: () => {},
							},
							{
								name: 'Orders',
								key: 'orders',
								current: module === 'orders',
								disabled: true,

								//onClick: () => {},
							},
							{
								name: 'Requests',
								key: 'requests',
								current: module === 'requests',
								disabled: true,
								//onClick: () => {},
							},
						].map((tab) => (
							<div
								key={tab.name}
								onClick={() => {
									if (!tab.disabled) {
										navigate(`/consumables/${tab?.key || ''}`);
										if (typeof tab.onClick === 'function') {
											tab.onClick();
										}
									}
								}}
								className={classNames(
									tab.current
										? 'border-indigo-500 text-indigo-600'
										: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
									'flex whitespace-nowrap border-b-2 py-4 px-1 text-xs font-medium cursor-pointer items-center',
									tab.disabled
										? 'cursor-default text-gray-300 hover:text-gray-300 hover:border-transparent'
										: ''
								)}
								aria-current={tab.current ? 'page' : undefined}
							>
								{tab.name}
								{tab.count ? (
									<span
										className={classNames(
											tab.current
												? 'bg-indigo-100 text-indigo-600'
												: 'bg-gray-100 text-gray-900',
											'ml-3 hidden rounded-full py-0.5 px-2.5 text-xxs font-medium md:inline-block'
										)}
									>
										{tab.count}
									</span>
								) : null}
							</div>
						))}
					</nav>
				</div>

				<Routes>
					<ProtectedRoute exact path={`/consumables`} component={Inventory} />
					<ProtectedRoute
						exact
						path={`/consumables/storage-points`}
						component={StoragePoints}
					/>

					<ProtectedRoute
						exact
						path={`/consumables/storage-points/add`}
						component={StoragePoints}
					/>

					<ProtectedRoute
						exact
						path={`/consumables/all`}
						component={Consumables}
						props={{ consumables, onRefreshConsumables }}
					/>

					<ProtectedRoute
						exact
						path={`/consumables/picklists`}
						component={Picklists}
					/>
					<ProtectedRoute
						exact
						path={`/consumables/picklists/:id`}
						component={Picklists}
					/>
					<ProtectedRoute
						exact
						path={`/consumables/:action`}
						component={Consumables}
						props={{
							consumables,
							onRefreshConsumables,
							params: defaultFilters,
						}}
					/>
				</Routes>
				<ViewConsumable />
			</MainContent>
		</>
	);
};

export default () => {
	return (
		<Routes>
			<ProtectedRoute
				exact
				path={`/consumables/storage-points/add`}
				component={BaseConsumables}
			/>
			<ProtectedRoute
				exact
				path={`/consumables/storage-points/:id`}
				component={StoragePoint}
			/>
			<ProtectedRoute
				path={`/consumables/storage-points`}
				component={BaseConsumables}
			/>
			<ProtectedRoute
				exact
				path={`/consumables/add`}
				component={BaseConsumables}
			/>
			<ProtectedRoute
				exact
				path={`/consumables/all`}
				component={BaseConsumables}
			/>
			<ProtectedRoute
				exact
				path={`/consumables/picklists`}
				component={BaseConsumables}
			/>
			<ProtectedRoute exact path={`/consumables/:id`} component={Consumable} />
			<ProtectedRoute path={`/consumables`} component={BaseConsumables} />
		</Routes>
	);
};
